/**
 * LanguageFlag: a round flag badge for a supported language.
 *
 * Renders the language's country flag (clipped to a circle) when one is
 * defined, and a neutral globe for region-neutral languages (e.g. Arabic
 * Generic) or unknown codes, so we never show a flag that misrepresents the
 * language. Flags are SVG React components from `country-flag-icons` (bundled
 * at build time, no network), so emoji are never used.
 *
 * @module components/LanguageFlag
 */

import React from 'react';
import { IconWorld } from '@tabler/icons-react';
import {
  US, FR, DE, IT, ES, BR, NL, PL, RU, TR, JP, KR, TH, VN, ID, IN, BD,
} from 'country-flag-icons/react/1x1';
import { getLanguageInfo, getLanguageName } from '../lib/languages';

/** A flag is one of country-flag-icons' SVG components (typed by the package). */
type FlagComponent = typeof US;

/**
 * Country code -> flag component. Only the flags actually used by
 * SUPPORTED_LANGUAGES are imported so the bundle stays small (the package is
 * side-effect-free, so unused flags tree-shake away).
 */
const FLAGS: Record<string, FlagComponent> = {
  US, FR, DE, IT, ES, BR, NL, PL, RU, TR, JP, KR, TH, VN, ID, IN, BD,
};

/** Props for {@link LanguageFlag}. */
export interface LanguageFlagProps {
  /** BCP-47 language code, e.g. 'en-US'. */
  code: string;
  /** Extra classes for the round badge (controls size; defaults to 1.25rem). */
  className?: string;
  /**
   * When true the badge is purely visual (no `role`/`aria-label`). Use inside
   * a control whose adjacent text already names the language, so screen readers
   * do not announce it twice.
   */
  decorative?: boolean;
}

/**
 * Round flag badge for a language code. By default the badge carries
 * `role="img"` and an `aria-label` with the language name; pass `decorative`
 * to hide it from assistive tech when neighbouring text already labels it.
 */
export const LanguageFlag: React.FC<LanguageFlagProps> = ({
  code,
  className = 'h-5 w-5',
  decorative = false,
}) => {
  const info = getLanguageInfo(code);
  const Flag = info?.countryCode ? FLAGS[info.countryCode] : undefined;
  const label = getLanguageName(code);

  return (
    <span
      data-testid="language-flag"
      data-country={info?.countryCode ?? 'none'}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-raised ${className}`}
    >
      {Flag ? (
        <Flag aria-hidden className="h-full w-full" />
      ) : (
        <IconWorld aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </span>
  );
};

export default LanguageFlag;
